import {_} from 'lodash';
import * as ko from "knockout";
import {global} from "../../../appContext";
import {AvailableLanguages, ILanguage, REQUEST_MESSAGE} from "../../../constants/Language";
import {IdName, IMessageData, IObjectType, ISyncField} from "../../../index";
import {createErrorMessage, createErrorString, downloadCSV, getObjectType, isPartOf} from "../../../util/misc";
import {ToggleAll} from "./ToggleAll";
import {ButtonManager} from "../../../util/ButtonManager";
import {getCancelButton, getContinueButton, getExitButton} from "../../../constants/RouteDefinitions";
import PromiseRequest from "../../../req/PromiseRequest";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {MAX_CONCURRENT_REQUESTS, SPINNER} from "../../../constants";
import {nsRoute} from "../../../util/route";
import {ISyncfieldsError, ISyncfieldsSuccess} from "../index";
import ButtonOrder = nsRoute.ButtonOrder;
import {storeObservable, storeObservableArray} from "../../../util/sessionstore";
import {ImportFormatId} from "../../../constants/ImportFormats";
import exportHtml from './export.html?raw';

export const template = exportHtml;

type TStep = 'init' | 'choose' | 'process' | 'finish'

type TSyncfieldValue = ISyncField[keyof ISyncField]

interface Dictionary<T> {
    [index: string]: T;
}

export class viewModel {

    private readonly req_getobjecttypes = global.getPromiseRequest('getobjecttypes')
    private readonly req_getimportformats = global.getPromiseRequest('getimportformats')

    private readonly availableLanguages: ILanguage[] = AvailableLanguages
    private availableObjectTypes!: IObjectType[];
    private availableImportFormats!: IdName[];

    private readonly selectedLanguages: KnockoutObservableArray<ILanguage> = ko.observableArray()
    private readonly selectedObjectTypes: KnockoutObservableArray<IObjectType> = ko.observableArray()
    private readonly selectedImportFormats: KnockoutObservableArray<IdName> = ko.observableArray()

    private toggleAllLanguages: ToggleAll<ILanguage>
    private toggleAllObjectTypes!: ToggleAll<IObjectType>
    private toggleAllImportFormats!: ToggleAll<IdName>

    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null)
    private readonly isReady: KnockoutObservable<boolean> = ko.observable(false)
    private readonly step: KnockoutObservable<TStep | null> = ko.observable(null)

    private readonly pageButtons = new ButtonManager()

    private requests!: PromiseRequest[]
    private successes!: ISyncfieldsSuccess[]
    private errors!: ISyncfieldsError[]
    private exportObjects!: ISyncField[][];
    private csv!: {
        contents: string,
        filename: string,
    }
    private percentComplete: KnockoutObservable<number | null> = ko.observable(null)

    constructor() {
        this.pageButtons.add({
            onClick: () => this.onDownloadClick(),
            enabled: () => !!this.successes?.length,
            icon: 'fa-download',
            label: '((save)) CSV',
            order: ButtonOrder.SUBMIT,
            validator: () => this.isStep('finish'),
        })
        const cancelBtn = getCancelButton('systemfieldsdetails', {
            validator: () => !this.isStep('finish'),
        })
        if (cancelBtn) {
            this.pageButtons.add(cancelBtn)
        }
        const exitBtn = getExitButton('systemfieldsdetails', {
            validator: () => this.isStep('finish'),
        })
        if (exitBtn) {
            this.pageButtons.add(exitBtn)
        }
        this.pageButtons.add(getContinueButton({
            onClick: () => {
                switch (this.step()) {
                    case "choose":
                        this.step('process')
                        this.process()
                        break;
                }
            },
            label: () => {
                const lenA = this.selectedLanguages().length;
                const lenB = this.selectedObjectTypes().length;
                const lenC = this.selectedImportFormats().length;
                return `((continue)) [${lenA * lenB * lenC}]`
            },
            enabled: (): boolean => {
                switch (this.step()) {
                    case 'choose':
                        return !!this.selectedLanguages().length && !!this.selectedObjectTypes().length && !!this.selectedImportFormats().length
                    default:
                        return false
                }
            },
            validator: () => this.isStep('choose'),
        }))

        this.toggleAllLanguages = new ToggleAll<ILanguage>({
            allItems: this.availableLanguages,
            selectedItems: this.selectedLanguages,
        })

        this.init()
    }

    private init() {

        this.step('init')
        this.messageData({message: REQUEST_MESSAGE})

        return Promise.resolve()
            .then(() => {
                return this.req_getobjecttypes.request()
                    .then((data: IObjectType[]) => {
                        this.availableObjectTypes = data
                        this.toggleAllObjectTypes = new ToggleAll({
                            allItems: this.availableObjectTypes,
                            selectedItems: this.selectedObjectTypes,
                        })
                    })
            })
            .then(() => {
                return this.req_getimportformats.request()
                    .then((data: IdName[]) => {
                        this.availableImportFormats = data
                        this.toggleAllImportFormats = new ToggleAll({
                            allItems: this.availableImportFormats,
                            selectedItems: this.selectedImportFormats,
                        })
                    })
            })
            .then(() => {
                const startingLanguages = storeObservableArray('syncfields.config.language')()
                    .map((id: ILanguage['id']) => this.availableLanguages.find(lang => lang.id == id))
                    .filter((lang): lang is ILanguage => !!lang)
                this.selectedLanguages(startingLanguages)

                const startingObjectType = this.availableObjectTypes.find(objectType => objectType.id == storeObservable('syncfields.config.objecttypeid')())
                if (startingObjectType) {
                    this.selectedObjectTypes([startingObjectType])
                }

                const startingImportFormat = this.availableImportFormats.find(importFormat => importFormat.id == storeObservable('syncfields.config.importformat', ImportFormatId.Visma)())
                if (startingImportFormat) {
                    this.selectedImportFormats([startingImportFormat])
                }

                this.step('choose')
                this.messageData(null)
                this.isReady(true)
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    private process() {

        this.percentComplete(null)
        this.requests = []
        this.successes = []
        this.errors = []

        const doNext = (): Promise<void> => {
            const numProcessed = this.successes.length + this.errors.length;
            this.percentComplete(numProcessed / numTotal)
            this.messageData({message: `((loading data|td)) [${numProcessed}/${numTotal}] ${SPINNER}`})
            if (!downloadParams.length) {
                return Promise.resolve()
            }
            const params = downloadParams.shift()
            if (!params) {
                return Promise.resolve()
            }
            const reqObj = nsSyncFields.getSyncFields(params.o.id, params.l.id, params.i.id)
            this.requests.push(reqObj.promise as PromiseRequest)
            return reqObj.request
                .then((data: ISyncField[]) => {
                    this.successes.push({
                        l: params.l,
                        o: params.o,
                        i: params.i,
                        syncfields: data,
                    })
                    return doNext()
                })
                .catch((reason: string) => {
                    this.errors.push({
                        l: params.l,
                        o: params.o,
                        i: params.i,
                        reason: reason,
                    })
                    return doNext()
                })
        }

        const downloadParams: { l: ILanguage, o: IObjectType, i: IdName }[] = []
        this.selectedLanguages().forEach((l: ILanguage) => {
            this.selectedObjectTypes().forEach((o: IObjectType) => {
                this.selectedImportFormats().forEach((i: IdName) => {
                    downloadParams.push({l, o, i})
                })
            })
        })
        const numTotal = downloadParams.length

        this.messageData({message: REQUEST_MESSAGE})
        const all = []
        for (let i = 0; i < Math.min(downloadParams.length, MAX_CONCURRENT_REQUESTS); i++) {
            all.push(doNext())
        }

        Promise.all(all).then(() => {
            this.exportObjects = _(this.successes)
                .map((o: ISyncfieldsSuccess) => o.syncfields)
                .flatten()
                .sortBy( // horizontal sorting
                    'languageid',
                    'importformatid',
                )
                .groupBy((o: ISyncField) => o.syncfieldid)
                .sortBy( // vertical sorting
                    (arr: ISyncField[]) => arr[0]?.objecttypeid ?? 0,
                    (arr: ISyncField[]) => arr[0]?.languageid ?? 0,
                    (arr: ISyncField[]) => arr[0]?.importformatid ?? 0,
                    (arr: ISyncField[]) => arr[0]?.syncfieldid ?? 0,
                )
                .value()

            const arrCommonKeys: (keyof ISyncField | string)[] = [
                'syncfieldid',
                'name',
                'objecttypeid',
                'objecttypename', // custom
            ]
            const arrSpecificKeys: (keyof ISyncField | string)[] = [
                'syncfielddisplaynameid',
                'languageid',
                'languagekey', // custom
                'importformatid',
                'importformatkey', // custom
                'displayname',
                'description',
                'core',
                'parameters',
            ]

            const table: any[] = []
            const tableHeadings: string[] = (() => {
                const ret: (keyof ISyncField | string)[] = []
                ret.push(...arrCommonKeys)
                this.selectedLanguages().forEach(() => {
                    this.selectedImportFormats().forEach(() => {
                        ret.push(...arrSpecificKeys)
                    })
                })
                return ret
            })()
            table.push(tableHeadings)

            const _key2val = (k: string, o: ISyncField): any => {
                switch (k) {
                    case 'objecttypename':
                        return getObjectType(o.objecttypeid).name
                    case 'languagekey':
                        return this.availableLanguages.find(_o => _o.id == o.languageid)?.key ?? ''
                    case 'importformatkey':
                        return this.availableImportFormats.find(_o => _o.id == o.importformatid)?.name ?? ''
                    default:
                        return o[k as keyof ISyncField]
                }
            }

            _(this.exportObjects)
                .forEach((syncfields: ISyncField[]) => {
                    const first = syncfields[0];
                    if (!first) {
                        return;
                    }
                    const row: any[] = []
                    row.push(...arrCommonKeys.map(k => _key2val(k, first)))
                    syncfields.forEach((sf: ISyncField) => {
                        // append all translations to the same row
                        row.push(...arrSpecificKeys.map(k => _key2val(k, sf)))
                    })
                    table.push(row)
                })

            const _getCsvRow = (arr: any[]): string => arr
                .map(val => String(val)
                    .replace(/"/gm, '""')
                    .replace(/\n/gm, '\\n')
                )
                .map((val: any): string => `"${val}"`)
                .join(',')

            this.csv = {
                contents: table.map(_getCsvRow).join('\n'),
                filename: [
                    'systemfields',
                    this.selectedLanguages().map(l => l.key).join('_'),
                    this.selectedObjectTypes().length,
                    this.selectedImportFormats().length
                ].join('_') + '.csv',
            }

            this.messageData(null)
            this.step('finish')

            if (this.successes.length) {
                this.onDownloadClick()
            }
        })
    }

    private onDownloadClick() {
        downloadCSV(this.csv.contents, this.csv.filename)
    }

    private isStep(...step: TStep[]) {
        const currentStep = this.step();
        return !!currentStep && isPartOf(currentStep, ...step)
    }

    private createErrorString(reason: string): string {
        return createErrorString(reason, '_blank')
    }

    dispose() {
        this.toggleAllLanguages.dispose()
        this.req_getobjecttypes.abort()
        this.req_getimportformats.abort()
        this.requests?.forEach(p => p.abort())
    }
}
